import { NavLink } from "react-router-dom";
import styled from "@emotion/styled/macro";
import { PropsWithChildren, ReactElement } from "react";
import { getNewPath } from "adapters/common";

type NavButtonProps = PropsWithChildren<{
  href: string;
  icon?: () => ReactElement;
  onClick?: () => void;
  isActive?: boolean;
  dataTestid?: string;
}>;

const NavButtonContainer = ({
  to,
  onClick,
  children,
  isActive,
  dataTestid,
}: PropsWithChildren<{
  to: string;
  onClick?: () => void;
  isActive?: boolean;
  dataTestid?: string;
}>) => {
  const path = getNewPath(to);

  return (
    <StyledNavLink
      to={path}
      onClick={onClick}
      isActive={isActive || false}
      data-testid={dataTestid}
    >
      {children}
    </StyledNavLink>
  );
};

const NavButton = ({
  children,
  href,
  icon,
  onClick,
  isActive,
  dataTestid,
}: NavButtonProps) => {
  return (
    <NavButtonContainer
      to={href}
      onClick={onClick}
      isActive={isActive}
      dataTestid={dataTestid}
    >
      <>
        {icon && icon()}
        {children && <NavText>{children}</NavText>}
      </>
    </NavButtonContainer>
  );
};

const StyledNavLink = styled(NavLink)<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${(props) => (props.isActive ? "#303551" : "transparent")};
  border: none;
  color: white;
  text-decoration: none;
  cursor: pointer;
  padding: 0;
  min-width: 0;
  border-radius: 12px;

  &:hover {
    background-color: ${(props) => (props.isActive ? "#303551" : "#22263A")};
  }

  /* Icon styling */
  svg {
    width: 24px;
    height: 24px;
    margin-bottom: 4px;
    flex-shrink: 0;
  }
`;

const NavText = styled.div`
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  text-align: center;
  color: white;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: capitalize;
  margin-top: 2px;
`;

export { NavButton };
